/**
 * Slack Deployment Settings Schema
 *
 * Zod schema for Deployment.deploymentSettings when type = SLACK.
 * Single source of truth for Slack deployment configuration.
 */

import { z } from "zod";

import type { DeepPartial } from "../../../shared/core/common.types";
import { BaseSettingsSchema } from "./Base.schema";

// ============================================================
// PENDING SLACK APP STATUS
// ============================================================

/**
 * Status values for pendingSlackApp.status.
 * "claiming"         = app creation in progress (transient, stale after CLAIM_STALE_MS)
 * "pending_approval" = app created, waiting for IT to approve OAuth install
 */
export const PENDING_SLACK_APP_STATUSES = ["claiming", "pending_approval"] as const;
export type PendingSlackAppStatus = (typeof PENDING_SLACK_APP_STATUSES)[number];

/** Named lookup for pendingSlackApp status values — use instead of hardcoded strings. */
export const PendingSlackAppStatus = {
  CLAIMING: "claiming" as const,
  PENDING_APPROVAL: "pending_approval" as const
} satisfies Record<string, PendingSlackAppStatus>;

// ============================================================
// CHANNEL MODE (Reusable constant)
// ============================================================

/**
 * Slack channel filtering modes
 * - all: Process messages from all channels
 * - dm_only: Only direct messages
 * - specific_channels: Only specified channel IDs
 * - pattern: Channels matching regex pattern
 */
export const SLACK_CHANNEL_MODES = ["all", "dm_only", "specific_channels", "pattern"] as const;
export const ChannelModeSchema = z.enum(SLACK_CHANNEL_MODES);
export type ChannelMode = z.infer<typeof ChannelModeSchema>;

// ============================================================
// MAIN SCHEMA (Complete Slack Deployment Settings)
// ============================================================

/**
 * Complete Slack Deployment Settings Schema
 *
 * This is the SINGLE SOURCE OF TRUTH for Deployment.deploymentSettings
 * when type = "SLACK". Stored as JSON in Prisma, validated with this schema.
 *
 * NOTE: agentId is NOT in deploymentSettings! It's a Deployment column (FK to Agent).
 * Agentid was moved from JSON to column. Pass agentId separately when creating deployments.
 */
export const SlackDeploymentSettingsSchema = z
  .object({
    // Channel filtering configuration
    channelMode: ChannelModeSchema.optional(),
    allowedChannels: z.array(z.string()).optional(), // Slack channel IDs: ["C123456", "C789012"]
    channelPattern: z.string().optional(), // Regex pattern: "support-.*"
    blockedChannels: z.array(z.string()).optional(), // Exclusion list

    // OAuth credentials (for manual/simple mode)
    signingSecret: z.string().optional(),
    clientId: z.string().optional(),
    clientSecret: z.string().optional(),

    // Pending connection ID - pre-generated UUID for webhook URL
    // Used during app creation flow, cleaned up after connection is created
    pendingConnectionId: z.string().optional(),

    // Pending app from a blocked OAuth attempt — stored so the user can retry
    // after IT approval without re-creating the Slack app.
    // Cleaned up in CompleteSlackAppInstallUseCase after successful OAuth.
    // SECURITY: only non-sensitive fields here — secrets stay in the ProviderHandshake.
    pendingSlackApp: z
      .object({
        appId: z.string(),
        clientId: z.string().min(1), // public — appears in OAuth URL; min(1) prevents empty-string falsy bypass
        teamId: z.string().optional(),
        teamName: z.string().optional(),
        workspaceConnectionId: z.string().optional(),
        handshakeId: z.string().optional(), // UUID pointing to the still-PENDING ProviderHandshake
        expiresAt: z.string().datetime().optional(), // ISO 8601 datetime — handshake expiry, used to detect stale sessions
        // .default("pending_approval") ensures backward compat: old DB records without status parse correctly
        status: z.enum(PENDING_SLACK_APP_STATUSES).default("pending_approval"),
        claimedAt: z.string().datetime().optional() // ISO timestamp set when status="claiming"; used for stale detection
      })
      .optional(),

    // Greeting message sent to the user before the first response in a new conversation.
    // null or omitted = disabled. Mirrors the embed's firstMessage behavior.
    firstMessage: z.string().nullable().optional(),

    // When true, post a Block Kit notification card in the thread whenever a workflow triggers.
    // Defaults to true (opt-out). Gives users visibility into what the agent is doing.
    showWorkflowNotifications: z.boolean().optional(),

    // When true, post a Block Kit notification card in the thread whenever a plugin/task/collection is used.
    // Defaults to false (opt-in). Shows which skills the agent activated and how long they took.
    showPluginNotifications: z.boolean().optional(),

    // When true, include a "View run" link in the completion card pointing to the Nexus run detail.
    // Sub-setting of showWorkflowNotifications (only meaningful when that is true).
    // Defaults to false — the link is only useful in private workspaces where all users
    // have a Nexus account. Enabling it in public workspaces exposes internal run URLs.
    // z.preprocess used to handle JSON that may have been coerced to string ("true"/"false")
    // by older serializers — strict z.boolean() would silently fail the whole schema parse.
    showWorkflowRunLink: z
      .preprocess((v) => (v === "true" ? true : v === "false" ? false : v), z.boolean())
      .optional(),

    // When true, add a 👀 reaction to the user's message while the bot is processing.
    // Requires the reactions:write scope on the Slack app. Defaults to true (opt-out).
    thinkingReaction: z.boolean().optional(),

    // When true, the bot replies in a thread when @mentioned in a channel instead of main channel.
    // Defaults to false — enable per deployment to avoid noise in busy channels.
    // Only applies to channel @mentions (app_mention events). For DMs, use dmRespondInThread.
    respondInThreadReplies: z.boolean().optional(),

    // When true, the bot replies in a thread on each DM message instead of the main conversation.
    // Defaults to false — DMs naturally flow in the main conversation.
    // Only applies to 1:1 DMs (channel_type === "im"). For channels, use respondInThreadReplies.
    dmRespondInThread: z.boolean().optional(),

    // Rotating messages shown in Slack's native typing indicator while the agent is processing.
    // Displayed via assistant.threads.setStatus() loadingMessages parameter.
    // Works in DMs and compatible group conversations. Max 5 messages, max 100 chars each.
    // Falls back to the 👀 emoji reaction (thinkingReaction) when unavailable.
    loadingMessages: z.array(z.string().max(100)).max(5).optional(),

    // When true, Slack will unfurl (show rich previews for) URLs in bot messages.
    // Defaults to false — keeps bot messages compact when they already contain URLs inline.
    // @see https://docs.slack.dev/messaging/unfurling-links-in-messages/
    unfurlLinks: z.boolean().optional(),

    // When true, Slack will unfurl media (images, videos, etc.) in bot messages.
    // Defaults to false — prevents duplicate media previews when URLs are listed in the body.
    unfurlMedia: z.boolean().optional(),

    // When true (default), a thread session inherits the full root channel history up to
    // the branch point. When false, the thread only sees the single message that started it.
    threadInheritHistory: z.boolean().optional()
  })
  .merge(BaseSettingsSchema);

export type SlackDeploymentSettings = z.infer<typeof SlackDeploymentSettingsSchema>;

export type PendingSlackApp = z.infer<typeof SlackDeploymentSettingsSchema>["pendingSlackApp"];

/**
 * Duration after which a "claiming" marker is considered stale and can be overwritten.
 * Guards against a process dying mid-creation without releasing the lock.
 */
export const CLAIM_STALE_MS = 5 * 60 * 1000; // 5 minutes

// ============================================================
// DEFAULTS (Single Source of Truth)
// ============================================================

/**
 * Default rotating messages for the Slack assistant thread typing indicator.
 * Single source of truth — imported by both backend webhook handler and frontend hook.
 */
export const DEFAULT_LOADING_MESSAGES: string[] = ["Processing your request..."];

/**
 * Default values for creating a new Slack deployment.
 * Used to initialize forms and ensure no missing fields.
 */
export const DEFAULT_SLACK_SETTINGS: SlackDeploymentSettings = {
  channelMode: "all",
  allowedChannels: [],
  blockedChannels: [],
  firstMessage: null,
  showWorkflowNotifications: false,
  showPluginNotifications: false,
  showWorkflowRunLink: false,
  thinkingReaction: true,
  respondInThreadReplies: false,
  dmRespondInThread: false,
  loadingMessages: DEFAULT_LOADING_MESSAGES,
  unfurlLinks: false,
  unfurlMedia: false,
  threadInheritHistory: false
};

// ============================================================
// FACTORY FUNCTION (Type-Safe Creation)
// ============================================================

/**
 * Create complete SLACK deployment settings with defaults.
 *
 * This is the RECOMMENDED way to create Slack deployment settings.
 * Ensures all required fields are present with sensible defaults.
 *
 * NOTE: agentId is now Deployment column (FK to Agent).
 * This function returns only the settings JSON - agentId is passed separately
 * when creating/updating a Deployment.
 *
 * @param overrides - Partial settings to override defaults (optional)
 * @returns Complete, type-safe SlackDeploymentSettings
 *
 * @example
 * // Create with defaults
 * const settings = createSlackDeploymentSettings();
 *
 * @example
 * // Create with overrides
 * const settings = createSlackDeploymentSettings({
 *   channelMode: "dm_only"
 * });
 */
export function createSlackDeploymentSettings(
  overrides?: DeepPartial<SlackDeploymentSettings>
): SlackDeploymentSettings {
  if (!overrides) return { ...DEFAULT_SLACK_SETTINGS };

  return {
    ...DEFAULT_SLACK_SETTINGS,
    ...overrides
  };
}

// ============================================================
// MANIFEST VERSION
// ============================================================

/**
 * Current Slack app manifest version.
 *
 * Increment this when SLACK_DEFAULT_BOT_SCOPES or SLACK_DEFAULT_BOT_EVENTS change.
 * Stored in ApiKeyConnection.settings.manifestVersion after a successful sync.
 * Bots with a lower (or missing) version are shown a "Bot permissions outdated" banner.
 *
 * Shared between backend (sync use cases) and frontend (banner condition).
 *
 * History:
 *   0 / undefined — Legacy bots (pre-2026-02-26, ~9 scopes, 3 events)
 *   1 — Comprehensive overhaul: 35 scopes + 14 events (2026-02-26)
 *   2 — Remove deprecated scopes: workflow.steps:execute (Slack removed 2024),
 *       assistant:write, assistant_thread_started, assistant_thread_context_changed
 *       (require features.assistant_view block — opt-in only). Net: 32 scopes + 12 events
 *   3 — Add assistant:write + assistant_thread_started + assistant_thread_context_changed
 *       + features.assistant_view block. Enables native DM typing indicator. Net: 33 scopes + 14 events
 */
export const SLACK_MANIFEST_VERSION = 3;
